import React, { useRef, useState, useMemo, useEffect, Suspense } from 'react';
import dynamic from 'next/dynamic';
import { Modal, Box, Button, Typography, TextField, Grid } from '@mui/material';
import { ContentData } from '@/utils/types';

// Dynamically import JoditEditor to ensure it only runs on the client side
//const JoditEditor = dynamic(() => import('jodit-react'), { ssr: false });
const JoditEditor = dynamic(() => import('jodit-react').then(mod => mod.default), { ssr: false });
//const JoditEditor = React.lazy(() => import('jodit-react'));

interface EditorModalProps {
  open: boolean;
  handleClose: () => void;
  mode: string;
  //initialContent: string;
  initialContent: ContentData;
  onSave: (data: { title: string; subtitle: string; content: string }) => void;
  placeholder?: string;
}

const EditorModal: React.FC<EditorModalProps> = ({ open, handleClose, mode, initialContent, onSave, placeholder }) => {
  const editor = useRef(null);
  const [content, setContent] = useState(initialContent.content);
  const [title, setTitle] = useState(initialContent.title);
  const [subtitle, setSubtitle] = useState(initialContent.subtitle);

  useEffect(() => {
    setContent(initialContent.content);
    setTitle(initialContent.title);
    setSubtitle(initialContent.subtitle);
  }, [initialContent]);

  const config = useMemo(
    () => ({
      iframe: true,
      height: '100%',
      //height: 600,
      readonly: false, // all options from https://xdsoft.net/jodit/docs/,
      placeholder: placeholder || 'Start typing...',
      defaultFontSizePoints: 'pt' as const, // Unit type: 'pt' or 'px'
      defaultFontFamily: 'Arial, sans-serif', // Default font family
      style: {
        font: '14pt Arial, sans-serif' // Inline style for font
      },
      useSearch: false,
      cleanHTML: {
        removeEmptyElements: false,
        removeEmptyNodes: false,
        removeEmptyBlocks: false,
      },
    }),
    [placeholder]
  );


  const handleSave = () => {
    onSave({title,subtitle,content});
    setTitle("");
    setSubtitle("");
    setContent("");
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -50%)', 
        width: '90%', 
        height: '90%', 
        bgcolor: 'background.paper', 
        boxShadow: 24, 
        p: 4, 
        display: 'flex', 
        flexDirection: 'column' 
      }}>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          {mode}
        </Typography>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
            <TextField
              sx={{ height: '0.5em', mt: -1 }}
              autoFocus
              id="title"
              placeholder={title}
              type="text"
              fullWidth
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Grid>
        </Grid>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
            <TextField
              sx={{ height: '0.5em', mt: -1 }}
              id="subtitle"
              placeholder='Subtitle'
              type="text"
              fullWidth
              variant="outlined"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
            />
          </Grid>
        </Grid>
        <Box sx={{ flexGrow: 1, mb: 2, height: '100%' }}>
        <Suspense fallback={<div>Loading...</div>}>
          <JoditEditor
            ref={editor}
            value={content}
            config={config}
            onBlur={newContent => setContent(newContent)} // preferred to use only this option to update the content for performance reasons
            onChange={newContent => {}}           
          />
          </Suspense>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            className="bg-blue-500 text-white rounded hover:bg-blue-600 mr-2"
            variant="contained"
            color="secondary"
            onClick={handleClose}            
          >
            Close
          </Button>
          <Button
            className="bg-blue-500 text-white rounded hover:bg-blue-600"
            variant="contained"
            color="primary"
            onClick={handleSave}            
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditorModal;